import type { JSX } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { KINGDOMS } from '@yuan-math/content';
import { SectionHeading } from '@yuan-math/ui';
import { Overworld } from '../../components/Overworld.tsx';
import { WorldMap } from '../../components/WorldMap.tsx';

/**
 * `/adventure/map` — the whole world on one screen (docs/rpg.md §22, §33).
 *
 * Two maps of one world, and they answer different questions. The overworld is
 * §22's: somewhere to *be*, walked across a tile at a time, where the calculus
 * is visibly a long way past the arithmetic and the only road north runs
 * through a mountain pass. The kingdom list under it is §33's: where am I, what
 * is open, what is sealed, and — in mathematics rather than in fantasy — why.
 *
 * The list is not a fallback and is not hidden behind a toggle. It is the only
 * place a seal can be stated in full ("Needs 82% of Starter Village skills —
 * you know 36%. Still to learn: Counting and Quantity…"), which is the sentence
 * §52 is about; a 42-pixel tile can carry a status word and nothing else. It is
 * also the route for anybody who cannot or would rather not walk, which is why
 * every kingdom on it is an ordinary link.
 */
export function WorldMapPage(): JSX.Element {
  /*
   * `?changed=<kingdomId>` — where a boss victory sends the student, so the
   * thing their fight changed is not left for them to find among ten kingdoms.
   * Read from the URL rather than from state so the highlight survives a
   * reload and can be shared, and validated against the real list so a
   * hand-typed id highlights nothing rather than something wrong.
   */
  const [params] = useSearchParams();
  const changed = params.get('changed') ?? undefined;
  const arrivedKingdomId = KINGDOMS.some((kingdom) => kingdom.id === changed) ? changed : undefined;

  return (
    <div className="page page--wide">
      <header className="page-header">
        <h1 className="page__title">World map</h1>
        <p className="page__lead">
          Ten kingdoms on one continent. Walk wherever you like — the sea and the mountains turn you back, and a sealed
          island keeps its story to itself until you have the skills it asks for. It keeps no mathematics: every lesson
          in every kingdom is yours to open right now, and the list below says exactly which skills open which island.
        </p>
      </header>

      <Overworld />

      <section className="dashboard-section">
        <SectionHeading
          title="The kingdoms"
          description="The same ten, in order, with what each one is waiting for said as mathematics — and every skill it names linked to its own lesson."
        />
        <WorldMap arrivedKingdomId={arrivedKingdomId} />
      </section>

      <div className="button-row">
        <Link to="/adventure" className="btn btn--secondary">
          Back to your adventure
        </Link>
      </div>
    </div>
  );
}
